import { useQuery } from '@tanstack/react-query';
import { adminSystemApi } from '@api/client';
import { useAuth } from '@hooks/useAuth';
import { useActiveCenter } from '@hooks/useActiveCenter';
import type { AccessMatrix } from '@api/types';
import { FRONTEND_ROLES, getNavMatrix } from './nav-config';

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'superadmin',
  center_admin: 'center_admin',
  staff: 'staff',
};

function SectionHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg)' }}>{title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function SessionTable() {
  const { user } = useAuth();
  const { activeCenterId, isSuper } = useActiveCenter();
  const rows: [string, string][] = [
    ['rol', user?.role ?? '—'],
    ['email', user?.email ?? '—'],
    ['userId', user?.id ?? '—'],
    ['centerId (JWT)', user?.centerId ?? '— (sin centro asignado)'],
  ];
  if (isSuper) {
    rows.push(['centro activo', activeCenterId === 'all' ? '— (todos los centros)' : activeCenterId]);
  }
  return (
    <table className="table" style={{ marginBottom: 0 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ width: 110, color: 'var(--fg-muted)', fontSize: 12 }}>{k}</td>
            <td className="mono" style={{ fontSize: 12 }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BackendMatrixTable({ matrix }: { matrix: AccessMatrix }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>resource</th>
            {matrix.roles.map((role) => (
              <th key={role}>{ROLE_LABELS[role] ?? role}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.resources.map((resource) => (
            <tr key={resource}>
              <td className="mono" style={{ fontSize: 12 }}>{resource}</td>
              {matrix.roles.map((role) => {
                const allowed = matrix.backendMatrix[role][resource];
                return (
                  <td key={role} className="mono" style={{ fontSize: 11.5, color: allowed.length === 0 ? 'var(--fg-soft)' : 'var(--fg)' }}>
                    {allowed.length === 0 ? '—' : allowed.join(', ')}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FrontendMatrixTable() {
  const rows = getNavMatrix();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>sección</th>
            <th className="mono" style={{ fontSize: 11 }}>path</th>
            {FRONTEND_ROLES.map((role) => (
              <th key={role} style={{ textAlign: 'center' }}>{ROLE_LABELS[role]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.label}</td>
              <td className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{row.path}</td>
              {FRONTEND_ROLES.map((role) => {
                const allowed = row.allowedRoles.includes(role);
                return (
                  <td key={role} style={{ textAlign: 'center', color: allowed ? 'var(--accent)' : 'var(--fg-soft)' }}>
                    {allowed ? '✓' : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Read-only debug panel for superadmin. Three sections:
 *   1. "Tu sesión"          — current JWT claims (role/email/userId/centerId).
 *   2. "Capacidades por rol" — backend matrix from /api/admin/system/access-matrix.
 *   3. "Secciones admin"     — frontend NAV matrix derived from nav-config.ts.
 *
 * The matrix is hardcoded in code (src/domain/staff/permissions.ts +
 * frontend/src/apps/admin/nav-config.ts); this panel surfaces it without
 * exposing any way to mutate it.
 */
export function AccessControlCard() {
  const { data, isLoading, isError } = useQuery<AccessMatrix>({
    queryKey: ['admin', 'access-matrix'],
    queryFn: () => adminSystemApi.accessMatrix(),
    staleTime: 60_000 * 60, // matrix is in-code; cache for an hour.
  });

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Control de acceso</span>
        <span style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>solo consulta</span>
      </div>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 22 }}>

        <div>
          <SectionHeader
            title="Tu sesión"
            sub="Claims del JWT actual. Útil para depurar problemas de visibilidad."
          />
          <SessionTable />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
          <SectionHeader
            title="Capacidades por rol (backend)"
            sub="Acciones permitidas por rol sobre cada recurso. Definidas en src/domain/staff/permissions.ts."
          />
          {isLoading && <p style={{ fontSize: 12, color: 'var(--fg-muted)', margin: 0 }}>Cargando…</p>}
          {isError && <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>No se pudo cargar la matriz.</p>}
          {data && <BackendMatrixTable matrix={data} />}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
          <SectionHeader
            title="Secciones admin por rol (frontend)"
            sub="Qué entradas del menú lateral ve cada rol. Derivado de nav-config.ts."
          />
          <FrontendMatrixTable />
        </div>

      </div>
    </div>
  );
}
